import Phaser from "phaser";
import { CANVAS_COLOURS } from "daedalus-core";
import { forEachCollisionRun, type CollisionTint } from "./collision-runs.ts";

export type { CollisionTint };

/**
 * Overlay of a map's effective collision grid (ADR-028) at the compiled cell (ADR-113): one `Graphics` object per
 * visible chunk, filled with the colour of the lowest set bit the game named, the cells of a row that take one
 * colour drawn as one rectangle. A cell the author painted by
 * hand (a collision layer byte) also gets a diagonal hatch, so hand-drawn blocks read apart from what
 * rules and footprints derive. A cell the author opened with the game's open value, and that nothing
 * blocks, is filled with the open colour and hatched, so a way over water reads apart from bare ground. Off
 * by default; the scene paints the chunks it shows and repaints the
 * ones a command touched.
 */

const ALPHA = 0.45;
const HATCH_ALPHA = 0.7;
const OPEN_ALPHA = 0.75;
const HATCH = CANVAS_COLOURS.collisionHatch;
const UNTINTED = CANVAS_COLOURS.collisionUntinted;

/**
 * Owns the overlay textures of one map.
 */
export class CollisionView {
  private readonly live = new Map<string, Phaser.GameObjects.Graphics>();
  private shown = false;

  /**
   * @param scene Scene to draw into.
   * @param cellPixels A cell's side in pixels: the compiled cell, which is what the grid holds.
   * @param chunkSize A chunk's side in cells.
   * @param tints Colours per flag bit, highest priority first.
   * @param depth Draw depth, above the map layers.
   * @param open The game's open mask and the colour of a cell it opens (ADR-028's amendment of 2026-10-04):
   *   a cell that does not block and whose painted byte holds a bit of the mask is filled with it, more
   *   opaque than a blocking tint so it holds over any ground. Null when
   *   the game declares no mask.
   */
  public constructor(
    private readonly scene: Phaser.Scene,
    private readonly cellPixels: number,
    private readonly chunkSize: number,
    private readonly tints: readonly CollisionTint[],
    private readonly depth: number,
    private readonly open: CollisionTint | null = null,
  ) {}

  /**
   * Whether the overlay is shown.
   *
   * @returns True when visible.
   */
  public get visible(): boolean {
    return this.shown;
  }

  /**
   * Shows or hides every chunk of the overlay.
   *
   * @param visible Whether to show.
   */
  public setVisible(visible: boolean): void {
    this.shown = visible;
    for (const graphics of this.live.values()) {
      graphics.setVisible(visible);
    }
  }

  /**
   * Draws one chunk of the grid.
   *
   * @param cx Chunk column.
   * @param cy Chunk row.
   * @param grid Whole-map collision bytes.
   * @param width Map width in cells of this grid.
   * @param height Map height in cells of this grid.
   * @param painted Bytes painted by hand, the same size as `grid`; those cells are hatched. Null hatches nothing.
   * @param propOpened Cells a placed prop opens with its footprint's passage (ADR-017's amendment of
   *   2026-10-05), the same size as `grid`: one that does not block is filled with the open colour, unhatched,
   *   since hatching means paint. Null for none.
   */
  public paint(cx: number, cy: number, grid: Uint8Array, width: number, height: number, painted: Uint8Array | null = null, propOpened: Uint8Array | null = null): void {
    const key = `${cx},${cy}`;
    let graphics = this.live.get(key);
    if (graphics === undefined) {
      graphics = this.scene.add.graphics().setDepth(this.depth).setVisible(this.shown);
      this.live.set(key, graphics);
    }
    graphics.clear();
    const span = { x0: cx * this.chunkSize, y0: cy * this.chunkSize, x1: Math.min((cx + 1) * this.chunkSize, width), y1: Math.min((cy + 1) * this.chunkSize, height) };
    const cell = this.cellPixels;
    const hatched: number[] = [];
    forEachCollisionRun(
      grid,
      width,
      span,
      this.tints,
      this.open,
      painted,
      propOpened,
      (x, y, length, tint, opened) => {
        graphics.fillStyle(tint?.color ?? UNTINTED.rgb, tint === null ? UNTINTED.alpha * ALPHA : opened ? OPEN_ALPHA : ALPHA);
        graphics.fillRect(x * cell, y * cell, length * cell, cell);
      },
      (x, y) => hatched.push(x, y),
    );
    graphics.lineStyle(1, HATCH.rgb, HATCH.alpha * HATCH_ALPHA);
    for (let i = 0; i < hatched.length; i += 2) {
      const x = hatched[i] as number;
      const y = hatched[i + 1] as number;
      graphics.lineBetween(x * cell, (y + 1) * cell, (x + 1) * cell, y * cell);
    }
  }

  /**
   * Whether a chunk is drawn.
   *
   * @param cx Chunk column.
   * @param cy Chunk row.
   * @returns True when painted.
   */
  public has(cx: number, cy: number): boolean {
    return this.live.has(`${cx},${cy}`);
  }

  /**
   * Releases a chunk's overlay.
   *
   * @param cx Chunk column.
   * @param cy Chunk row.
   */
  public release(cx: number, cy: number): void {
    const key = `${cx},${cy}`;
    this.live.get(key)?.destroy();
    this.live.delete(key);
  }
}
